import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Eye,
  MousePointerClick,
  CalendarClock,
  Sparkles,
  ArrowRight,
  AlertCircle,
  TrendingUp,
  Share2,
  Gift,
  Copy,
  Repeat,
  XCircle,
  Play,
} from "lucide-react";
import { toast } from "sonner";
import Sparkline from "@/components/Sparkline";

import "./SponsorDashboard.css";

const SURFACE_LABEL = {
  global_footer: "Global footer rail",
  event_main_banner: "Event 'Presented by' banner",
  table_card: "Table cards",
};

const nowMs = Date.now();

// Static demo data
const STATIC_SPONSORSHIPS = {
  "sponsor@techcorp.com": {
    email: "sponsor@techcorp.com",
    sponsorships: [
      {
        id: 1,
        company: "TechCorp",
        tier: "Global Annual",
        scope: "global",
        status: "approved",
        payment_status: "paid",
        amount: 25000,
        currency: "USD",
        impressions_30d: 145620,
        clicks_30d: 2847,
        ctr_pct: 1.95,
        days_remaining: 287,
        expires_at: new Date(Date.now() + 287 * 24 * 60 * 60 * 1000).toISOString(),
        auto_renew: true,
        cancel_at_period_end: false,
        billing_mode: "subscription",
        sparkline_7d: [1200, 1350, 1280, 1420, 1380, 1510, 1490],
        by_surface: {
          global_footer: { impressions: 85430, clicks: 1678 },
          event_main_banner: { impressions: 45120, clicks: 892 },
          table_card: { impressions: 15070, clicks: 277 },
        },
        moment_views_30d: 523,
        moment_avg_watched_sec: 18.4,
        moment_avg_total_sec: 30,
        moment_completion_pct: 61.3,
        referral_code: "TECHCORP2024",
        pending_credit: 2500,
        referral_count: 2,
      },
    ],
  },
  "partner@greentide.io": {
    email: "partner@greentide.io",
    sponsorships: [
      {
        id: 2,
        company: "Greentide",
        tier: "Event Title",
        scope: "event",
        status: "approved",
        payment_status: "paid",
        amount: 5000,
        currency: "USD",
        impressions_30d: 28340,
        clicks_30d: 612,
        ctr_pct: 2.16,
        days_remaining: null,
        expires_at: null,
        auto_renew: false,
        cancel_at_period_end: false,
        billing_mode: "one_time",
        sparkline_7d: [420, 380, 510, 490, 530, 470, 540],
        by_surface: {
          event_main_banner: { impressions: 18240, clicks: 394 },
          table_card: { impressions: 10100, clicks: 218 },
        },
        moment_views_30d: 0,
        moment_avg_watched_sec: 0,
        moment_avg_total_sec: 0,
        moment_completion_pct: 0,
        referral_code: null,
        pending_credit: 0,
        referral_count: 0,
      },
    ],
  },
  "marketing@venturefund.com": {
    email: "marketing@venturefund.com",
    sponsorships: [
      {
        id: 3,
        company: "Venture Fund",
        tier: "Global Annual",
        scope: "global",
        status: "approved",
        payment_status: "paid",
        amount: 25000,
        currency: "USD",
        impressions_30d: 198450,
        clicks_30d: 3876,
        ctr_pct: 1.95,
        days_remaining: 18,
        expires_at: new Date(Date.now() + 18 * 24 * 60 * 60 * 1000).toISOString(),
        auto_renew: false,
        cancel_at_period_end: false,
        billing_mode: "subscription",
        sparkline_7d: [1580, 1620, 1590, 1710, 1680, 1750, 1820],
        by_surface: {
          global_footer: { impressions: 112340, clicks: 2201 },
          event_main_banner: { impressions: 62180, clicks: 1214 },
          table_card: { impressions: 23930, clicks: 461 },
        },
        moment_views_30d: 842,
        moment_avg_watched_sec: 22.8,
        moment_avg_total_sec: 45,
        moment_completion_pct: 50.7,
        referral_code: "VFUND10",
        pending_credit: 5000,
        referral_count: 4,
      },
    ],
  },
};

function Stat({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="sdStat" data-testid={testId}>
      <div className="sdStatTop">
        {Icon && <Icon size={14} className="sdStatIcon" />}
        <p className="overline sdStatOverline">{label}</p>
      </div>
      <p className="font-display sdStatValue">{value}</p>
      {sub && <p className="sdStatSub">{sub}</p>}
    </div>
  );
}

function SponsorshipCard({ sp, token, onChanged }) {
  const scopeCls =
    sp.scope === "global"
      ? "sdBadge sdBadgeScope sdBadgeScope--global"
      : sp.scope === "event"
        ? "sdBadge sdBadgeScope sdBadgeScope--event"
        : "sdBadge sdBadgeScope sdBadgeScope--table";

  const statusCls =
    sp.status === "approved"
      ? "sdBadge sdBadgeStatus sdBadgeStatus--approved"
      : sp.status === "pending"
        ? "sdBadge sdBadgeStatus sdBadgeStatus--pending"
        : sp.status === "declined"
          ? "sdBadge sdBadgeStatus sdBadgeStatus--declined"
          : "sdBadge sdBadgeStatus";

  return (
    <div data-testid={`sponsorship-card-${sp.id}`} className="sdCard">
      <div className="sdCardHeader">
        <div>
          <div className="sdBadgesRow">
            <span className={scopeCls}>{sp.tier}</span>
            <span className={statusCls}>{sp.status}</span>

            {sp.payment_status && sp.payment_status !== "paid" && (
              <span className="sdBadge sdBadgePaymentWarn">
                Payment: {sp.payment_status}
              </span>
            )}
          </div>

          <h3 className="font-display sdCompany">{sp.company}</h3>
        </div>

        <div className="sdAmount">
          <p className="font-display sdAmountNum">
            ${Math.round(sp.amount).toLocaleString()}
          </p>
          <p className="sdAmountCur">{sp.currency}</p>
        </div>
      </div>

      <div className="sdStatsGrid">
        <Stat
          icon={Eye}
          label="Impressions · 30 days"
          value={sp.impressions_30d.toLocaleString()}
        />
        <Stat
          icon={MousePointerClick}
          label="Click-throughs"
          value={sp.clicks_30d.toLocaleString()}
          sub={`${sp.ctr_pct}% CTR`}
        />
        {sp.scope === "global" && sp.days_remaining !== null ? (
          <Stat
            icon={CalendarClock}
            label="Days remaining"
            value={sp.days_remaining}
            sub={sp.expires_at ? new Date(sp.expires_at).toLocaleDateString() : ""}
          />
        ) : (
          <Stat
            icon={CalendarClock}
            label="Term"
            value={sp.scope === "global" ? "Annual" : "Per event"}
          />
        )}
        <Stat icon={Sparkles} label="Scope" value={sp.scope} />
      </div>

      <div className="sdSparklineBox">
        <div className="sdSparklineHeader">
          <p className="overline sdSparklineOverline">
            <TrendingUp size={11} className="sdGoldIcon" /> Last 7 days · impressions
          </p>
        </div>
        <Sparkline
          data={sp.sparkline_7d || []}
          width={320}
          height={70}
          testId={`sparkline-${sp.id}`}
        />
      </div>

      <div className="sdSurfaceBox">
        <p className="overline sdSurfaceOverline">Where your logo is being seen</p>
        <div className="sdSurfaceGrid">
          {Object.entries(sp.by_surface).map(([surf, stats]) => (
            <div
              key={surf}
              className="sdSurfaceTile"
              data-testid={`surface-${surf}-${sp.id}`}
            >
              <p className="sdSurfaceLabel">{SURFACE_LABEL[surf] || surf}</p>
              <div className="sdSurfaceMetrics">
                <div>
                  <p className="font-display sdSurfaceValue">
                    {stats.impressions.toLocaleString()}
                  </p>
                  <p className="sdSurfaceSmall">views</p>
                </div>
                <div>
                  <p className="font-display sdSurfaceValue sdSurfaceValueGold">
                    {stats.clicks.toLocaleString()}
                  </p>
                  <p className="sdSurfaceSmall">clicks</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {(sp.moment_views_30d || 0) > 0 && (
        <div
          className="sdMomentBox"
          data-testid={`moment-stats-${sp.id}`}
        >
          <p className="overline sdMomentOverline">
            <Play size={11} className="sdFillCurrent" /> Sponsor moment · last 30 days
          </p>

          <div className="sdMomentGrid">
            <Stat
              icon={Play}
              label="Moment plays"
              value={sp.moment_views_30d.toLocaleString()}
              testId={`moment-views-${sp.id}`}
            />

            {sp.moment_avg_total_sec > 0 ? (
              <Stat
                icon={TrendingUp}
                label="Avg. watched"
                value={`${sp.moment_avg_watched_sec.toFixed(1)}s`}
                sub={
                  sp.moment_avg_total_sec > 0
                    ? `of ${sp.moment_avg_total_sec.toFixed(0)}s total`
                    : ""
                }
                testId={`moment-watched-${sp.id}`}
              />
            ) : (
              <Stat
                icon={TrendingUp}
                label="Avg. watched"
                value="—"
                sub="iframe embed (no duration)"
                testId={`moment-watched-${sp.id}`}
              />
            )}

            <Stat
              icon={Sparkles}
              label="Completion"
              value={sp.moment_avg_total_sec > 0 ? `${sp.moment_completion_pct.toFixed(0)}%` : "—"}
              sub={sp.moment_avg_total_sec > 0 ? "watched ≥90%" : "iframe — no data"}
              testId={`moment-completion-${sp.id}`}
            />
          </div>
        </div>
      )}

      {sp.billing_mode === "subscription" && (
        <SubscriptionBlock sp={sp} token={token} onChanged={onChanged} />
      )}

      {sp.scope === "global" &&
        sp.days_remaining !== null &&
        sp.days_remaining < 30 &&
        !sp.auto_renew && (
          <div className="sdExpiryWarn">
            <AlertCircle size={16} className="sdExpiryWarnIcon" />
            <p className="sdExpiryWarnText">
              Your Global sponsorship expires in{" "}
              <strong>{sp.days_remaining} days</strong>. Renew to keep your logo
              on every vmeetio surface.
            </p>
            <Link to="/sponsor" className="sdExpiryWarnCta">
              Renew <ArrowRight size={12} />
            </Link>
          </div>
        )}

      {sp.referral_code && <ReferralBlock sp={sp} />}
    </div>
  );
}

function SubscriptionBlock({ sp, token, onChanged }) {
  const [busy, setBusy] = useState(false);
  const renewsOn = sp.expires_at
    ? new Date(sp.expires_at).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "—";
  const endsOn = renewsOn;
  const isCanceling = sp.cancel_at_period_end;
  const isActive = sp.auto_renew && !isCanceling;

  const cancel = async () => {
    if (!token) {
      toast.error(
        "Use your magic-link email to cancel — open this dashboard from that link."
      );
      return;
    }
    const ok = window.confirm(
      `Cancel auto-renew? You'll keep all sponsor perks until ${endsOn}. After that, your logo stops appearing across vmeetio.`
    );
    if (!ok) return;

    setBusy(true);
    await new Promise((resolve) => setTimeout(resolve, 800));

    try {
      toast.success(`Auto-renew cancelled. You're covered until ${endsOn}.`);
      if (onChanged) onChanged();
    } catch {
      toast.error("Could not cancel — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sdSubBox" data-testid={`subscription-block-${sp.id}`}>
      <div className="sdSubTop">
        <div className="sdSubLeft">
          <Repeat size={18} className="sdSubIcon" />
          <div>
            <p className="overline sdSubOverline">
              {isActive ? "Auto-renew · active" : "Auto-renew · cancelled"}
            </p>
            <p className="sdSubBody">
              {isActive ? (
                <>
                  Stripe will charge{" "}
                  <strong>${Math.round(sp.amount).toLocaleString()}</strong> on{" "}
                  <strong>{renewsOn}</strong> and renew your Global sponsorship
                  for another year.
                </>
              ) : (
                <>
                  You're covered until <strong>{endsOn}</strong>. After that,
                  the subscription will not renew.
                </>
              )}
            </p>
          </div>
        </div>

        {isActive && (
          <button
            onClick={cancel}
            disabled={busy}
            data-testid={`subscription-cancel-${sp.id}`}
            className="sdCancelBtn"
            type="button"
          >
            <XCircle size={12} /> {busy ? "Cancelling…" : "Cancel auto-renew"}
          </button>
        )}
      </div>
    </div>
  );
}

function ReferralBlock({ sp }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = `${window.location.origin}/sponsor?ref=${sp.referral_code}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied to clipboard.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy. Select manually below.");
    }
  };

  return (
    <div className="sdRefBox" data-testid={`referral-block-${sp.id}`}>
      <div className="sdRefTop">
        <div>
          <p className="overline sdRefOverline">
            <Gift size={11} className="sdGreenIcon" /> Refer another brand
          </p>
          <p className="sdRefBody">
            Share your link — they save 10%, and you earn a 10% credit toward
            your next renewal.
          </p>
        </div>

        <div className="sdRefCredit">
          <p className="font-display sdRefCreditNum" data-testid={`pending-credit-${sp.id}`}>
            ${sp.pending_credit.toFixed(2)}
          </p>
          <p className="sdRefCreditSub">
            Pending credit · {sp.referral_count} referral
            {sp.referral_count === 1 ? "" : "s"}
          </p>
        </div>
      </div>

      <div className="sdRefLinkRow">
        <code className="sdRefCode" data-testid={`referral-url-${sp.id}`}>
          {shareUrl}
        </code>

        <button
          onClick={copy}
          data-testid={`copy-referral-${sp.id}`}
          className="sdRefCopyBtn"
          type="button"
        >
          {copied ? "Copied" : (
            <>
              <Copy size={11} /> Copy
            </>
          )}
        </button>

        <a
          href={`mailto:?subject=${encodeURIComponent("Sponsor vmeetio")}&body=${encodeURIComponent(
            `Thought you'd like vmeetio — here's my referral link (10% off): ${shareUrl}`
          )}`}
          className="sdRefEmailBtn"
        >
          <Share2 size={11} /> Email
        </a>
      </div>
    </div>
  );
}

export default function SponsorDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get("token") || "";
  const emailFromUrl = searchParams.get("email") || "";

  const [email, setEmail] = useState(emailFromUrl);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [linkSent, setLinkSent] = useState(null); // { email, dev_link? }

  const loadByToken = async (token) => {
    setLoading(true);
    setSearched(true);

    await new Promise((resolve) => setTimeout(resolve, 800));

    const fakeEmail = "sponsor@techcorp.com";
    const found = STATIC_SPONSORSHIPS[fakeEmail];

    if (found) {
      setData(found);
    } else {
      setData(null);
      toast("No sponsorships found.");
    }
    setLoading(false);
  };

  const loadByEmail = async (em) => {
    if (!em) return;
    setLoading(true);
    setSearched(true);

    await new Promise((resolve) => setTimeout(resolve, 800));

    try {
      const normalizedEmail = em.trim().toLowerCase();
      const found = STATIC_SPONSORSHIPS[normalizedEmail];

      if (found) {
        setData(found);
        setSearchParams({ email: em }, { replace: true });
      } else {
        setData(null);
        toast("No sponsorships found for that email.");
      }
    } catch {
      setData(null);
      toast.error("Couldn't load dashboard.");
    } finally {
      setLoading(false);
    }
  };

  const requestLink = async (e) => {
    e.preventDefault();
    if (!email) return;

    await new Promise((resolve) => setTimeout(resolve, 600));

    const normalizedEmail = email.trim().toLowerCase();
    const hasData = !!STATIC_SPONSORSHIPS[normalizedEmail];

    if (hasData) {
      const devLink = `?email=${encodeURIComponent(normalizedEmail)}`;
      setLinkSent({ email: normalizedEmail, dev_link: devLink });
      toast("Dev mode — link shown below.");
    } else {
      toast.error("No sponsorships found for that email.");
    }
  };

  useEffect(() => {
    if (tokenFromUrl) {
      loadByToken(tokenFromUrl);
    } else if (emailFromUrl) {
      loadByEmail(emailFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      data-testid="sponsor-dashboard"
      className="sdPage"
      style={{ background: "linear-gradient(180deg, #061412 0%, #0A1D1A 100%)" }}
    >
      <div className="container-x sdContainer">
        <p className="overline sdTopOverline">Sponsor dashboard</p>

        <h1 className="font-display sdH1">Your logo, by the numbers.</h1>

        <p className="sdTopBody">
          Type the email you used at checkout — we'll send you a private link to
          your dashboard. The link is valid for 30 days, so you can bookmark it.
        </p>

        {!data && (
          <form onSubmit={requestLink} className="sdEmailForm">
            <input
              type="email"
              placeholder="sponsor@techcorp.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="sponsor-dashboard-email"
              className="sdEmailInput"
              required
            />

            <button
              type="submit"
              disabled={loading}
              data-testid="sponsor-dashboard-submit"
              className="btn-gold sdEmailBtn"
            >
              {loading ? "Loading…" : (
                <>
                  Send my link <ArrowRight size={14} />
                </>
              )}
            </button>
          </form>
        )}

        {linkSent && (
          <div className="sdLinkSent" data-testid="sponsor-link-sent">
            <p className="sdLinkSentText">
              <strong>Link sent</strong> to{" "}
              <span className="sdLinkSentEmail">{linkSent.email}</span>. Open it
              from your inbox to view live impression counts.
            </p>

            {linkSent.dev_link && (
              <p className="sdDevNote">
                Dev mode (no SMTP configured) —{" "}
                <a
                  href={linkSent.dev_link}
                  className="sdDevLink"
                  data-testid="sponsor-dev-link"
                >
                  click to open the dashboard directly
                </a>
                .
              </p>
            )}
          </div>
        )}

        {data && data.sponsorships.length > 0 && (
          <div data-testid="sponsor-dashboard-results">
            <p className="sdResultsMeta">
              {data.sponsorships.length} active sponsorship
              {data.sponsorships.length === 1 ? "" : "s"} for{" "}
              <span className="sdResultsEmail">{data.email}</span>.
            </p>

            {data.sponsorships.map((sp) => (
              <SponsorshipCard
                key={sp.id}
                sp={sp}
                token={tokenFromUrl}
                onChanged={() => loadByToken(tokenFromUrl)}
              />
            ))}
          </div>
        )}

        {searched && !loading && (!data || data.sponsorships.length === 0) && (
          <div className="sdEmpty" data-testid="sponsor-dashboard-empty">
            <p className="sdEmptyText">No sponsorships found for that email yet.</p>
            <Link to="/sponsor" className="btn-gold sdEmptyCta">
              Become a sponsor <ArrowRight size={14} />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}